import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { ToastItem, ToastType } from './toast.types';

interface ToastProps {
  toast: ToastItem;
  onDismiss: (id: string) => void;
}

/** Icones en trait fin, une par etat. */
const ToastIcon: React.FC<{ type: ToastType }> = ({ type }) => {
  switch (type) {
    case 'success':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      );
    case 'error':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      );
    case 'warning':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M12 16v-4M12 8h.01" />
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
};

const Toast: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const { id, type, title, message, actions, dismissible, loading, duration, exiting } = toast;
  const [paused, setPaused] = useState(false);
  const remainingRef = useRef<number>(duration);

  const close = useCallback(() => onDismiss(id), [id, onDismiss]);

  // Le toast peut etre mis a jour en place (meme id) : ex. saving(persistant) ->
  // saved(2500ms). Le reliquat doit alors repartir sur la nouvelle duree, sinon
  // il garde l'ancienne (0 = persistant) et ne se ferme jamais / se ferme aussitot.
  // Declare AVANT l'effet minuteur pour s'executer en premier au meme rendu.
  useEffect(() => {
    remainingRef.current = duration;
  }, [duration]);

  // Disparition auto avec pause au survol : le temps restant est decrémenté a
  // chaque pause, puis le minuteur repart avec ce reliquat au survol suivant.
  useEffect(() => {
    if (duration <= 0 || exiting || paused) {
      return;
    }
    const start = Date.now();
    const remaining = remainingRef.current;
    const timer = window.setTimeout(close, remaining);
    return () => {
      remainingRef.current = remaining - (Date.now() - start);
      window.clearTimeout(timer);
    };
  }, [duration, exiting, paused, close]);

  const hasTimer = duration > 0 && !exiting;

  return (
    <div
      className={`toast toast--${type}${loading ? ' toast--loading' : ''}${actions?.length ? ' toast--wide' : ''}${exiting ? ' toast--exit' : ''}`}
      role={type === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span className="toast__icon" aria-hidden="true">
        {loading ? <span className="toast__spinner" /> : <ToastIcon type={type} />}
      </span>

      <div className="toast__body">
        {title && <div className="toast__title">{title}</div>}
        {message && <div className="toast__message">{message}</div>}

        {!!actions?.length && (
          <div className="toast__actions">
            {actions.map((a, i) => (
              <button
                key={i}
                type="button"
                className={`toast__btn toast__btn--${a.variant ?? 'secondary'}`}
                onClick={() => {
                  a.onClick();
                  if (a.dismissOnClick !== false) {
                    close();
                  }
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {dismissible && (
        <button type="button" className="toast__close" onClick={close} aria-label="Fermer la notification">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      )}

      {hasTimer && (
        <span
          className="toast__progress"
          style={{ animationDuration: `${duration}ms`, animationPlayState: paused ? 'paused' : 'running' }}
        />
      )}
    </div>
  );
};

export default Toast;
